import { fmtTimeIL } from "../recordings/utils";

export const MAX_CLIP_SEC = 15 * 60;
export const MIN_CLIP_SEC = 10;

// App.js declares `.rtl * { direction: rtl }`, and author CSS on `*` outranks
// the UA's mapping for the dir attribute — so `dir="ltr"` is inert in Hebrew and
// a range like 12:30–13:00 renders as 13:00–12:30 (the dash is a bidi neutral
// between two numeric runs). Only the CSS property actually pins it.
export const LTR = { direction: "ltr", unicodeBidi: "isolate" };

export function fmtClock(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h ? String(m).padStart(2, "0") : String(m);
  return `${h ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}

// Every time on this page is an offset into the recording, because that is what
// the <video> element's own control bar counts and we cannot change it. Showing
// broadcast clock time as the editable value put two time bases on one screen —
// the player said 0:00 while the field said 09:00:00. Wall clock survives only
// as a caption, never as a number you type.
export function fmtWall(recordStartUtcMs, offsetSec, withSeconds = true) {
  return new Date(recordStartUtcMs + offsetSec * 1000).toLocaleTimeString("en-GB", {
    timeZone: "Asia/Jerusalem",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    ...(withSeconds ? { second: "2-digit" } : {}),
  });
}

// Every edit to the range goes through one of these, so the invariants hold no
// matter which control made the change: both edges inside bounds, and at least
// MIN_CLIP_SEC apart. Directional, because the edge the user just set is the
// one that must survive — marking an in-point should push the out-point, not
// silently drag the in-point back.
function squeeze(bounds, anchored, other, isStart) {
  const lo = bounds.min;
  const hi = bounds.max;
  const a = Math.min(Math.max(anchored, lo), hi);
  if (isStart) {
    const end = Math.min(hi, Math.max(other, a + MIN_CLIP_SEC));
    return [Math.round(Math.min(a, end - MIN_CLIP_SEC)), Math.round(end)];
  }
  const start = Math.max(lo, Math.min(other, a - MIN_CLIP_SEC));
  return [Math.round(start), Math.round(Math.max(a, start + MIN_CLIP_SEC))];
}

export const withStart = (bounds, start, end) => squeeze(bounds, start, end, true);
export const withEnd = (bounds, start, end) => squeeze(bounds, end, start, false);

// Parse what fmtClock prints, plus bare seconds. Returns null on anything
// unusable so the caller rejects it instead of guessing.
export function parseClock(text, maxSec) {
  const parts = String(text ?? "").trim().split(":");
  if (parts.length > 3 || parts.some((p) => !/^\d+$/.test(p))) return null;

  const [h, m, s] =
    parts.length === 3
      ? parts.map(Number)
      : parts.length === 2
        ? [0, ...parts.map(Number)]
        : [0, 0, Number(parts[0])];
  if (parts.length > 1 && (s > 59 || (parts.length === 3 && m > 59))) return null;

  const total = h * 3600 + m * 60 + s;
  return total > maxSec ? null : total;
}

// The timeline always spans the whole recording, because the <video> element's
// own scrubber does and the two sit inches apart. An earlier version scoped it
// to the show window for precision, which made the bar end at 20:00 while the
// video kept playing to 30:00 — precision is not worth a timeline that lies.
// `videoDurationSec` wins when known: the mp4's real length drifts from the
// scheduled window at HLS segment boundaries.
export function clipBounds(show, videoDurationSec) {
  if (!show) return { min: 0, max: 0 };
  const max =
    Number.isFinite(videoDurationSec) && videoDurationSec > 0
      ? videoDurationSec
      : show.recordingDurationSec;
  return { min: 0, max: Math.round(max) };
}

// Israel-local calendar day of a UTC instant, as YYYY-MM-DD. en-CA formats as
// ISO, which is what makes it sortable.
export function dayKeyIL(ms) {
  return new Date(ms).toLocaleDateString("en-CA", { timeZone: "Asia/Jerusalem" });
}

export function groupByDay(shows) {
  const days = new Map();
  for (const show of shows || []) {
    const key = dayKeyIL(show.showStartUtcMs);
    const bucket = days.get(key);
    if (bucket) bucket.push(show);
    else days.set(key, [show]);
  }
  return [...days.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([dayKey, items]) => ({
      dayKey,
      shows: items.sort((a, b) => a.showStartUtcMs - b.showStartUtcMs),
    }));
}

// "Today" / "Yesterday" / weekday, resolved against Israel time so the tab
// labels match the broadcast day the rows were bucketed into.
export function dayLabel(dayKey, text, nowMs = Date.now()) {
  if (dayKey === dayKeyIL(nowMs)) return text.today;
  if (dayKey === dayKeyIL(nowMs - 86400000)) return text.yesterday;
  const [y, m, d] = dayKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
}

export const showWindow = (show) =>
  `${fmtTimeIL(show.showStartUtcMs)}–${fmtTimeIL(show.showEndUtcMs)}`;
